import { Button, Spinner } from '@consigli/facade';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilePdf } from 'react-icons/fa';

import { formatPageNumbers } from './contradiction-utils';
import { HighlightedText } from './highlighted-text';
import { SeverityBadge, type Severity } from './severity-badge';

interface DocumentSourceCardProps {
  id: string;
  fileName?: string | null;
  pageNumbers?: number[] | null;
  pageIndex?: number | null;
  confidenceScore?: number | null;
  contradictionSeverity?: Severity | null;
  chunkText?: string;
  highlightText?: string | null;
  contradictionReason?: string | null;
  blobId?: string | null;
  isLoadingSource?: boolean;
  onViewSource?: () => void;
}

export const DocumentSourceCard: FC<DocumentSourceCardProps> = ({
  id,
  fileName,
  pageNumbers,
  pageIndex,
  confidenceScore,
  contradictionSeverity,
  chunkText,
  highlightText,
  contradictionReason,
  blobId,
  isLoadingSource = false,
  onViewSource,
}) => {
  const { t } = useTranslation();
  const canViewSource = !!blobId && !!onViewSource;

  const severityLabels = {
    high: t('meeting-secretary.contradictions.severity-high'),
    medium: t('meeting-secretary.contradictions.severity-medium'),
    low: t('meeting-secretary.contradictions.severity-low'),
  };

  return (
    <div key={id} className="p-3 rounded border bg-meeting-200 border-meeting-700">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {fileName && (
          <span className="text-xs px-2 py-0.5 rounded font-medium bg-meeting-700 text-white">
            {fileName}
          </span>
        )}
        {(pageIndex != null || (pageNumbers && pageNumbers.length > 0)) && (
          <span className="text-xs text-gray-600">
            {pageIndex != null ? `Page ${pageIndex + 1}` : formatPageNumbers(pageNumbers)}
          </span>
        )}
        {confidenceScore && (
          <span className="text-xs text-gray-500">
            {t('meeting-secretary.contradictions.confidence', {
              percent: Math.round(confidenceScore * 100),
            })}
          </span>
        )}
        {contradictionSeverity && (
          <SeverityBadge
            severity={contradictionSeverity}
            showLabel={false}
            severityLabels={severityLabels}
          />
        )}
        {canViewSource && (
          <Button
            tertiary
            icon={isLoadingSource ? undefined : FaFilePdf}
            iconSize={12}
            disabled={isLoadingSource}
            onClick={onViewSource}
            className="ml-auto !py-0.5 !px-2 !text-xs text-meeting-700 hover:text-meeting-900"
            title={t('meeting-secretary.contradictions.view-in-document')}
          >
            {isLoadingSource && <Spinner size="xsmall" role="status" className="!h-3 !w-3" />}
            {t('meeting-secretary.contradictions.view-in-document')}
          </Button>
        )}
      </div>

      {chunkText && (
        <div className="mb-2 p-2 bg-white/50 rounded">
          <p className="text-xs text-meeting-700 font-medium mb-1">
            {t('meeting-secretary.contradictions.document-text')}:
          </p>
          <p className="text-sm text-gray-700 italic">
            &quot;
            <HighlightedText text={chunkText} highlight={highlightText} />
            &quot;
          </p>
        </div>
      )}

      {contradictionReason && (
        <p className="text-sm text-gray-800">
          <span className="font-medium">
            {t('meeting-secretary.contradictions.why-contradicts')}{' '}
          </span>
          {contradictionReason}
        </p>
      )}
    </div>
  );
};
